"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { CampaignCard } from "./CampaignCard";
import { getDisplayStatus, FILTERABLE_STATUSES } from "@/lib/campaign-status";
import { CAMPAIGN_CATEGORIES, type CategoryId } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type CampaignCardData = {
  id: string;
  title: string;
  headline?: string;
  description: string;
  slug: string;
  orgName: string;
  locationText: string;
  categories: CategoryId[];
  chain?: string;
  coverImageUrl?: string | null;
  clueCount: number;
  claimCount: number;
  status: "DRAFT" | "PUBLISHED" | "ACTIVE" | "ENDED" | "ARCHIVED";
  startAt?: string | Date | null;
  endAt?: string | Date | null;
  createdAt: Date;
  campaignType?: "SINGLE_LOCATION" | "MULTI_LOCATION";
  latitude?: number | null;
  longitude?: number | null;
  tenant: {
    name: string;
    slug: string;
    logoUrl?: string | null;
  };
};

function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

interface CampaignGridProps {
  campaigns: CampaignCardData[];
  emptyMessage?: string;
  initialCategories?: CategoryId[];
}

export function CampaignGrid({ campaigns, emptyMessage, initialCategories = [] }: CampaignGridProps) {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedChain, setSelectedChain] = useState("all");
  const [selectedLocationType, setSelectedLocationType] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState<CategoryId[]>(initialCategories);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [locationSearch, setLocationSearch] = useState("");
  const [searchRadius, setSearchRadius] = useState<string>("none");
  const [searchCoords, setSearchCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geocoding, setGeocoding] = useState(false);

  const availableChains = useMemo(() => {
    const chains = new Set(campaigns.map((c) => c.chain).filter(Boolean) as string[]);
    return Array.from(chains).sort();
  }, [campaigns]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const geocodeLocation = async () => {
    if (!locationSearch.trim()) {
      setSearchCoords(null);
      return;
    }
    setGeocoding(true);
    try {
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(locationSearch)}&key=${apiKey}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setSearchCoords({ lat, lng });
      } else {
        setSearchCoords(null);
      }
    } catch {
      setSearchCoords(null);
    } finally {
      setGeocoding(false);
    }
  };

  const toggleCategory = (id: CategoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const removeCategory = (id: CategoryId) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== id));
  };

  const clearFilters = () => {
    setSelectedStatus("all");
    setSelectedChain("all");
    setSelectedLocationType("all");
    setSelectedCategories([]);
    setLocationSearch("");
    setSearchRadius("none");
    setSearchCoords(null);
  };

  const hasActiveFilters = selectedStatus !== "all" || selectedChain !== "all" || selectedLocationType !== "all" || selectedCategories.length > 0 || searchCoords !== null;

  const filtered = useMemo(() => {
    let result = campaigns;
    if (selectedStatus !== "all") {
      result = result.filter((c) => getDisplayStatus(c) === selectedStatus);
    }
    if (selectedChain !== "all") {
      result = result.filter((c) => c.chain === selectedChain);
    }
    if (selectedCategories.length > 0) {
      result = result.filter((c) =>
        selectedCategories.some((cat) => c.categories.includes(cat))
      );
    }
    if (selectedLocationType !== "all") {
      result = result.filter((c) => c.campaignType === selectedLocationType);
    }
    if (searchCoords && searchRadius !== "none") {
      const radiusMiles = parseInt(searchRadius);
      result = result.filter((c) => {
        if (!c.latitude || !c.longitude) return false;
        return haversineDistance(searchCoords.lat, searchCoords.lng, c.latitude, c.longitude) <= radiusMiles;
      });
    }
    return result;
  }, [campaigns, selectedStatus, selectedChain, selectedCategories, selectedLocationType, searchCoords, searchRadius]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          All Campaigns ({filtered.length})
        </h2>

        {/* All filters in one row */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Category multi-select dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryDropdownOpen((prev) => !prev)}
              className="inline-flex items-center border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Categories
              {selectedCategories.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-medium">
                  {selectedCategories.length}
                </span>
              )}
              <svg className="ml-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {categoryDropdownOpen && (
              <div className="absolute left-0 z-10 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {CAMPAIGN_CATEGORIES.map((category) => {
                  const isSelected = selectedCategories.includes(category.id);
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => toggleCategory(category.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50 ${
                        isSelected ? "bg-blue-50" : ""
                      }`}
                    >
                      <span className={`mr-2 flex items-center justify-center w-4 h-4 rounded border ${
                        isSelected
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-300"
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                      <span className="mr-2">{category.icon}</span>
                      <span className="text-gray-700">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Chain filter */}
          {availableChains.length > 1 && (
            <select
              value={selectedChain}
              onChange={(e) => setSelectedChain(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
            >
              <option value="all">All Chains</option>
              {availableChains.map((chain) => (
                <option key={chain} value={chain}>
                  {chain}
                </option>
              ))}
            </select>
          )}

          {/* Location type filter */}
          <select
            value={selectedLocationType}
            onChange={(e) => setSelectedLocationType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
          >
            <option value="all">All Types</option>
            <option value="SINGLE_LOCATION">Single Location</option>
            <option value="MULTI_LOCATION">Multi-Location</option>
          </select>

          {/* Status filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
          >
            <option value="all">All Status</option>
            {FILTERABLE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {/* Divider */}
          <div className="hidden sm:block w-px h-8 bg-gray-300" />

          {/* Location search */}
          <div className="flex items-end gap-2">
            <Input
              type="text"
              placeholder="City, state, or zip..."
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") geocodeLocation();
              }}
              className="w-48"
            />
            <select
              value={searchRadius}
              onChange={(e) => setSearchRadius(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
            >
              <option value="none">Any Distance</option>
              <option value="5">5 miles</option>
              <option value="15">15 miles</option>
              <option value="25">25 miles</option>
              <option value="50">50 miles</option>
            </select>
            <Button
              onClick={geocodeLocation}
              disabled={geocoding || !locationSearch.trim()}
              size="sm"
            >
              {geocoding ? "..." : "Search"}
            </Button>
            {searchCoords && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setLocationSearch("");
                  setSearchRadius("none");
                  setSearchCoords(null);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Active filter indicators */}
        {(searchCoords && searchRadius !== "none" || selectedCategories.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {searchCoords && searchRadius !== "none" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                Within {searchRadius} mi of &quot;{locationSearch}&quot;
                <button
                  type="button"
                  onClick={() => {
                    setLocationSearch("");
                    setSearchRadius("none");
                    setSearchCoords(null);
                  }}
                  className="ml-0.5 hover:text-green-900"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            {selectedCategories.map((catId) => {
              const cat = CAMPAIGN_CATEGORIES.find((c) => c.id === catId);
              if (!cat) return null;
              return (
                <span
                  key={catId}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                  <button
                    type="button"
                    onClick={() => removeCategory(catId)}
                    className="ml-0.5 hover:text-blue-900"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((campaign) => (
            <CampaignCard key={campaign.id} campaign={campaign} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            No campaigns found
          </h3>
          <p className="text-gray-600 mb-6">
            {emptyMessage || "No campaigns match your current filters."}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
